/**
 * Blog Post 8: Hong Kong Fixed Deposit Interest Rates Comparison February 2026
 * 香港定期存款利率比較 2026年2月
 *
 * Rate data is fetched from the backend API (/api/v1/deposit-rates/).
 * To update rates, edit: backend/app/data/deposit_rates.json
 */
import { useEffect, useState } from 'react';
import { useCustomTranslation } from '../hooks';
import { Box, Container, Typography } from '@mui/material';
import { trackPageView } from '../utils/analytics';
import { depositRatesApi } from '../services/api';
import SEO from '../components/common/SEO';
import AmazonRecommendation from '../components/common/AmazonRecommendation';
import type { DepositRatesData, BankRateEntry, HighlightItem } from '../services/api';

// ── Shared styles ──
const tableContainerStyle = {
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '30px',
  overflowX: 'auto' as const,
};
const tableStyle = { width: '100%', borderCollapse: 'collapse' as const, fontSize: '12px', minWidth: '900px' };
const thStyle = (extra?: React.CSSProperties): React.CSSProperties => ({
  textAlign: 'center', padding: '10px 5px', color: '#1e293b', fontWeight: '600', ...extra,
});
const thStickyStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 5px', color: '#1e293b', fontWeight: '600',
  position: 'sticky', left: 0, background: '#f8fafc', minWidth: '130px',
};
const tdCenter = (color: string, bold = false): React.CSSProperties => ({
  textAlign: 'center', padding: '9px 5px', color, fontWeight: bold ? '600' : '400',
});
const tdSticky = (bg: string): React.CSSProperties => ({
  padding: '9px 5px', color: '#334155', fontWeight: '600', position: 'sticky', left: 0, background: bg,
});
const tdNotes: React.CSSProperties = {
  padding: '9px 5px', color: '#64748b', fontSize: '11px', maxWidth: '180px', lineHeight: '1.4',
};
const rowBorder = '1px solid #e2e8f0';

const G = '#16a34a'; // green for rates
const D = '#475569'; // default grey for missing

// ── Helper: render a rate cell ──
function RateCell({ value }: { value: string | null }) {
  if (!value) return <td style={tdCenter(D)}>—</td>;
  return <td style={tdCenter(G, true)}>{value}</td>;
}

// ── Helper: render a single bank row ──
function BankRow({ bank, index, lang }: { bank: BankRateEntry; index: number; lang: 'zh' | 'en' }) {
  const isEven = index % 2 === 1;
  const bg = isEven ? '#f8fafc' : '#fff';
  return (
    <tr style={{ borderBottom: rowBorder, background: isEven ? '#f8fafc' : undefined }}>
      <td style={tdSticky(bg)}>{bank.name[lang]}</td>
      <RateCell value={bank.rates['1m']} />
      <RateCell value={bank.rates['3m']} />
      <RateCell value={bank.rates['6m']} />
      <RateCell value={bank.rates['12m']} />
      <td style={tdCenter('#475569')}>{bank.minDeposit[lang]}</td>
      <td style={tdNotes}>{bank.notes[lang]}</td>
    </tr>
  );
}

// ── Helper: render a highlight item ──
function HighlightBullet({ h }: { h: HighlightItem }) {
  return (
    <li>
      <strong>{h.bank}：</strong>
      {h.tenor} 高達{' '}
      <span style={{ color: G, fontWeight: h.featured ? '700' : '600', fontSize: h.featured ? '18px' : undefined }}>
        {h.rate}
      </span>
      {h.condition ? `（${h.condition}）` : ''}
    </li>
  );
}
function HighlightBulletEn({ h }: { h: HighlightItem }) {
  return (
    <li>
      <strong>{h.bank}:</strong> Up to{' '}
      <span style={{ color: G, fontWeight: h.featured ? '700' : '600', fontSize: h.featured ? '18px' : undefined }}>
        {h.rate}
      </span>
      {' '}({h.tenor}{h.condition ? `, ${h.condition}` : ''})
    </li>
  );
}

export default function BlogPost8() {
  const { i18n } = useCustomTranslation();
  const isEnglish = i18n.language === 'en';
  const lang: 'zh' | 'en' = isEnglish ? 'en' : 'zh';

  const [data, setData] = useState<DepositRatesData | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    trackPageView('/blog/hong-kong-fixed-deposit-rates-comparison-february-2026', 'Blog Post 8');
    document.title = isEnglish
      ? 'Hong Kong Fixed Deposit Interest Rates Comparison February 2026 | PayWise365'
      : '香港定期存款利率比較 2026年2月 | PayWise365';
  }, [isEnglish]);

  useEffect(() => {
    let cancelled = false;
    depositRatesApi.getRates()
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError('Failed to load deposit rates'); });
    return () => { cancelled = true; };
  }, []);

  const seoTitle = isEnglish
    ? 'Hong Kong Fixed Deposit Interest Rates Comparison February 2026 | PayWise365'
    : '香港定期存款利率比較 2026年2月 | PayWise365';

  const seoDescription = isEnglish
    ? 'Compare fixed deposit interest rates from major Hong Kong banks in February 2026. Find the best HKD and USD deposit rates, including promotional offers from HSBC, Hang Seng, Standard Chartered, and more.'
    : '比較2026年2月香港主要銀行定期存款利率。尋找最佳港幣及美元存款利率，包括滙豐、恒生、渣打等銀行的推廣優惠。';

  const seoKeywords = isEnglish
    ? 'Hong Kong fixed deposit rates, 定期存款利率, time deposit HK, HKD deposit rates, USD deposit rates, bank interest rates 2026, best deposit rates'
    : '香港定期存款, 定期利率, 銀行利率比較, 港元定存, 美元定存, 2026存款利率';

  // ── Loading / error states ──
  if (error) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }
  if (!data) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">Loading rates…</Typography>
      </Box>
    );
  }

  const lastUpdated = data.lastUpdated; // e.g. "2026-02-06"
  const highlights = data.highlights[lang];
  const traditional = data.traditionalBanks;
  const virtual = data.virtualBanks;

  // ── Table builder used for both Chinese & English ──
  const renderTable = (
    banks: BankRateEntry[],
    headerLabel: string,
    tenorHeaders: string[],
    minDepositHeader: string,
    notesHeader: string,
    sourceText: string,
  ) => (
    <div style={tableContainerStyle}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ borderBottom: '2px solid #cbd5e1' }}>
            <th style={thStickyStyle}>{headerLabel}</th>
            {tenorHeaders.map((h) => <th key={h} style={thStyle()}>{h}</th>)}
            <th style={thStyle({ minWidth: '90px' })}>{minDepositHeader}</th>
            <th style={thStyle({ textAlign: 'left', minWidth: '150px' })}>{notesHeader}</th>
          </tr>
        </thead>
        <tbody>
          {banks.map((bank, i) => (
            <BankRow key={bank.id} bank={bank} index={i} lang={lang} />
          ))}
        </tbody>
      </table>
      <p style={{ fontSize: '12px', color: '#64748b', marginTop: '15px', marginBottom: 0 }}>
        {sourceText}
      </p>
    </div>
  );

  // ── Shared i18n strings ──
  const tenorZh = ['1個月', '3個月', '6個月', '1年'];
  const tenorEn = ['1 Month', '3 Months', '6 Months', '12 Months'];
  const sourceZh = `資料來源：各銀行官方網站及公開比較平台（${lastUpdated} 更新）| 註：「—」表示數據未有公開或需向銀行查詢`;
  const sourceEn = `Source: Official bank websites & public comparison platforms (Updated ${lastUpdated}) | Note: "—" indicates data not publicly available; check with bank directly`;

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://www.paywise365.com/blog/hong-kong-fixed-deposit-rates-february-2026"
        ogType="article"
        publishDate="2026-02-15T00:00:00+08:00"
        modifiedDate="2026-02-15T00:00:00+08:00"
        articleSection={isEnglish ? 'Banking Rates' : '銀行利率'}
        tags={isEnglish ? ['Fixed Deposit', 'Interest Rates', 'Banking'] : ['定期存款', '利率', '銀行']}
      />
      {/* Page Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            {isEnglish ? 'Hong Kong Fixed Deposit Interest Rates Comparison February 2026' : '香港定期存款利率比較 2026年2月'}
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.95, fontWeight: 400 }}>
            {isEnglish ? 'All major banks comparison - Latest rates updated February 2026' : '全港主要銀行比較 - 2026年2月最新利率'}
          </Typography>
        </Container>
      </Box>

      {/* Article Content */}
      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 } }}>
        <Box
          component="article"
          sx={{
            lineHeight: 1.8,
            '& p': { mb: 2 },
            '& h2': { mt: 4, mb: 2, fontSize: '1.75rem', fontWeight: 700 },
            '& h3': { mt: 3, mb: 1.5, fontSize: '1.5rem', fontWeight: 600 },
            '& ul, & ol': { mb: 2, pl: 3 },
            '& li': { mb: 1 },
          }}
        >
          {!isEnglish ? (
            /* ================================================================
               CANTONESE CONTENT
               ================================================================ */
            <>
              <div style={{ marginBottom: '30px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff', color: '#2563eb', padding: '6px 14px',
                    borderRadius: '14px', fontWeight: '500', marginRight: '12px'
                  }}>銀行儲蓄</span>
                  <span>發佈日期：{lastUpdated}</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>10分鐘閱讀</span>
                </div>
                <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b', marginTop: '20px', lineHeight: '1.3' }}>
                  港元定期存款利率比較2026│全港主要銀行最新定存優惠
                </h1>
                <p style={{ fontSize: '18px', color: '#64748b', marginTop: '15px' }}>
                  踏入2026年2月，香港各大銀行同虛擬銀行繼續提供具競爭力嘅定期存款利率。本文整合全港主要銀行嘅最新港元定存優惠，助你揀選最適合嘅定存方案。
                </p>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>
                2026年2月最新定存利率概覽
              </h2>

              <p style={{ marginBottom: '20px', color: '#475569' }}>
                根據各銀行官方網站及公開資料（截至{lastUpdated}），港元定期存款涵蓋一日至24個月，部分銀行起存門檻低至HK$1，最高利率可達<strong>3.00%</strong>以上。以下表格綜合整理自各銀行官網及比較平台資料。
              </p>

              <div style={{
                background: '#fef3c7', border: '1px solid #fde047',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>
                  🏆 2026年2月最高利率精選
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  {highlights.map((h, i) => <HighlightBullet key={i} h={h} />)}
                </ul>
              </div>

              {/* ─── 傳統銀行 ─── */}
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                傳統銀行定期存款利率（2026年2月）
              </h2>
              <p style={{ marginBottom: '20px', color: '#475569' }}>
                傳統銀行雖然一般定存利率略低於虛擬銀行，但勝在分行網絡廣泛，而且針對新資金提供特惠利率。
              </p>

              {renderTable(traditional, '傳統銀行', tenorZh, '最低存款', '備註 / 條件', sourceZh)}

              {/* ─── 虛擬銀行 ─── */}
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                虛擬銀行定期存款利率（2026年2月）
              </h2>
              <p style={{ marginBottom: '20px', color: '#475569' }}>
                虛擬銀行憑住低營運成本，普遍提供較傳統銀行更高嘅定存利率，而且起存門檻極低，部分低至1港元起存。
              </p>

              {renderTable(virtual, '虛擬銀行', tenorZh, '最低存款', '備註 / 條件', sourceZh)}

              {/* ─── 如何選擇 ─── */}
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                如何選擇最適合你嘅定期存款
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                1. 虛擬銀行 vs 傳統銀行
              </h3>

              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <table style={{ width: '100%', fontSize: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #cbd5e1', color: '#1e293b' }}>類型</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #cbd5e1', color: '#1e293b' }}>優點</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #cbd5e1', color: '#1e293b' }}>缺點</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0', color: '#334155', fontWeight: '600' }}>虛擬銀行<br /><span style={{ fontSize: '12px', fontWeight: '400' }}>(PAObank、天星、Fusion)</span></td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                        • 最高利率可達 2.80%（視乎銀行及推廣）<br />
                        • 起存門檻超低（最低HK$1）<br />
                        • 24小時網上開立
                      </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                        • 無實體分行<br />
                        • 需習慣純數碼銀行服務
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 8px', color: '#334155', fontWeight: '600' }}>傳統銀行<br /><span style={{ fontSize: '12px', fontWeight: '400' }}>(匯豐、中銀、花旗)</span></td>
                      <td style={{ padding: '12px 8px', color: '#475569' }}>
                        • 品牌信譽高<br />
                        • 分行網絡廣泛<br />
                        • 新資金優惠吸引
                      </td>
                      <td style={{ padding: '12px 8px', color: '#475569' }}>
                        • 一般利率較低<br />
                        • 起存金額門檻較高
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                2. 新資金優惠要留意
              </h3>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                唔少銀行都為「新資金」提供特惠利率，通常比一般利率高0.2-0.5%。新資金一般定義為：
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li><strong>從其他銀行轉入嘅資金</strong></li>
                <li><strong>過去特定期間（如3個月）內未曾存入該銀行嘅資金</strong></li>
                <li><strong>需符合最低金額要求（如HK$10,000或以上）</strong></li>
              </ul>

              <div style={{
                background: '#eff6ff', border: '1px solid #3b82f6',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1e40af' }}>
                  💡 新資金定存小貼士
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  <li>存款前先了解清楚「新資金」定義</li>
                  <li>留意優惠期限，過期後利率可能大幅下降</li>
                  <li>計算埋提早提取罰款（如有）</li>
                  <li>考慮分散存款到2-3間銀行</li>
                </ul>
              </div>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                3. 選擇存款期限
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li><strong>1-3個月：</strong>適合短期資金停泊，保持靈活性，但利率通常較低</li>
                <li><strong>6個月：</strong>平衡回報同靈活性嘅好選擇</li>
                <li><strong>12個月：</strong>鎖定較長期回報，但要考慮利率走勢同資金需要</li>
                <li><strong>12個月以上：</strong>部分虛擬銀行提供長達24-48個月定存，適合長線儲蓄</li>
              </ul>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                4. 存款保障計劃
              </h3>
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#991b1b' }}>
                  🛡️ 重要：存款保障上限
                </h3>
                <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '10px' }}>
                  香港存款保障委員會為每間銀行嘅每名存戶提供最高<strong>HK$500,000</strong>保障。
                </p>
                <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: 0, fontWeight: '600' }}>
                  建議：如果存款超過HK$500,000，應分散存放喺2間或以上銀行，確保全數受保障。
                </p>
              </div>

              {/* ─── 資料來源 ─── */}
              <div style={{
                background: '#f0fdf4', border: '1px solid #86efac',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#166534' }}>
                  📋 資料來源
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '2', marginBottom: 0, fontSize: '13px' }}>
                  <li><a href="https://hk.ulifestyle.com.hk/topic/detail/20053976/%E9%A6%99%E6%B8%AF%E9%8A%80%E8%A1%8C%E6%B8%AF%E5%85%83%E5%AE%9A%E6%9C%9F%E5%AD%98%E6%AC%BE%E5%88%A9%E7%8E%87%E6%AF%94%E8%BC%83-%E6%9C%80%E6%96%B0%E6%B8%AF%E5%85%83%E5%AE%9A%E6%9C%9F%E9%AB%98%E6%81%AF%E4%B9%8B%E9%81%B8-%E6%AF%8F%E6%97%A5%E6%9B%B4%E6%96%B0" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>U Lifestyle 港元定期存款利率比較</a> — 每日更新（{lastUpdated}）</li>
                  <li><a href="https://www.stashaway.hk/r/best-hkd-time-deposit-interest-rates-and-offers" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>StashAway HK</a> — 定期存款利率及優惠比較</li>
                  <li><a href="https://www.syfe.com/hk/magazine/april-time-deposit-interest-rate-hk/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Syfe HK</a> — 港元定期存款利率每月更新</li>
                  <li><a href="https://www.hsbc.com.hk/accounts/products/time-deposits/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>HSBC 官方網站</a> — 定期存款產品頁</li>
                  <li><a href="https://www.bochk.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>中銀香港官方網站</a> — 定期存款利率</li>
                </ul>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              {/* ─── 免責聲明 ─── */}
              <div style={{
                background: '#f8fafc', borderTop: '3px solid #3b82f6',
                borderRadius: '8px', padding: '20px', marginTop: '40px'
              }}>
                <p style={{ marginBottom: '10px', fontSize: '14px', color: '#64748b' }}>
                  <strong>免責聲明：</strong>本文利率資料整理自各銀行官方網站及公開比較平台（截至{lastUpdated}更新），惟利率可能會在不通知的情況下更改，並會因客戶類型、存款金額、期限及推廣活動而有所不同。存款前必須直接向銀行核實最新利率及條款。本文僅供教育及參考用途，不構成財務建議。如需個人化指導，請諮詢持牌財務顧問。
                </p>
                <p style={{ marginBottom: 0, fontSize: '14px', color: '#64748b' }}>
                  <strong>數據來源：</strong>各銀行官方網站、StashAway HK、Syfe HK 及公開比較平台（{lastUpdated}更新）
                </p>
              </div>
            </>
          ) : (
            /* ================================================================
               ENGLISH CONTENT
               ================================================================ */
            <>
              <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '1px solid #e2e8f0' }}>
                <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '10px' }}>
                  <span style={{
                    background: '#eff6ff', color: '#2563eb', padding: '6px 14px',
                    borderRadius: '14px', fontWeight: '500', marginRight: '12px'
                  }}>Banking &amp; Savings</span>
                  <span>Published: {lastUpdated}</span>
                  <span style={{ margin: '0 8px' }}>•</span>
                  <span>10 min read</span>
                </div>
                <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#1e293b', marginTop: '20px', lineHeight: '1.3' }}>
                  Hong Kong Fixed Deposit Interest Rates Comparison February 2026
                </h1>
                <p style={{ fontSize: '18px', color: '#64748b', marginTop: '15px' }}>
                  As of February 2026, Hong Kong's major banks and virtual banks continue to offer competitive fixed deposit rates. This article compiles the latest HKD fixed deposit offers from all major banks to help you choose the best option.
                </p>
              </div>

              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                February 2026 Latest Fixed Deposit Rates Overview
              </h2>
              <p style={{ marginBottom: '20px', color: '#475569' }}>
                Based on official bank websites and public comparison platforms (as of {lastUpdated}), HKD fixed deposits range from one day to 24 months, with some banks allowing deposits as low as HK$1 and headline rates reaching <strong>3.00%</strong> or above. The tables below are compiled from official bank sources.
              </p>

              <div style={{
                background: '#fef3c7', border: '1px solid #fde047',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1e293b' }}>
                  🏆 Highest Rates in February 2026 (Selected Offers)
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  {highlights.map((h, i) => <HighlightBulletEn key={i} h={h} />)}
                </ul>
              </div>

              {/* ─── Traditional Banks ─── */}
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Traditional Banks Fixed Deposit Rates (February 2026)
              </h2>
              <p style={{ marginBottom: '20px', color: '#475569' }}>
                While traditional banks generally offer slightly lower fixed deposit rates than virtual banks, they excel with extensive branch networks and special rates for new funds.
              </p>

              {renderTable(traditional, 'Traditional Bank', tenorEn, 'Min Deposit', 'Notes / Conditions', sourceEn)}

              {/* ─── Virtual Banks ─── */}
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                Virtual Banks / E-Banks Fixed Deposit Rates (February 2026)
              </h2>
              <p style={{ marginBottom: '20px', color: '#475569' }}>
                Virtual banks leverage lower operating costs to generally offer higher fixed deposit rates than traditional banks, with extremely low minimum deposit requirements — some as low as HK$1.
              </p>

              {renderTable(virtual, 'Virtual Bank', tenorEn, 'Min Deposit', 'Notes / Conditions', sourceEn)}

              {/* ─── How to Choose ─── */}
              <h2 style={{ fontSize: '24px', fontWeight: '600', marginTop: '40px', marginBottom: '15px', color: '#1e293b' }}>
                How to Choose the Best Fixed Deposit for You
              </h2>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                1. Virtual Banks vs Traditional Banks
              </h3>
              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <table style={{ width: '100%', fontSize: '14px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #cbd5e1', color: '#1e293b' }}>Type</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #cbd5e1', color: '#1e293b' }}>Advantages</th>
                      <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #cbd5e1', color: '#1e293b' }}>Disadvantages</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0', color: '#334155', fontWeight: '600' }}>Virtual Banks<br /><span style={{ fontSize: '12px', fontWeight: '400' }}>(PAObank, Airstar, Fusion)</span></td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                        • Up to 2.80% (depends on bank &amp; promotions)<br />
                        • Ultra-low minimum (as low as HK$1)<br />
                        • 24/7 online opening
                      </td>
                      <td style={{ padding: '12px 8px', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                        • No physical branches<br />
                        • Need to adapt to digital-only banking
                      </td>
                    </tr>
                    <tr>
                      <td style={{ padding: '12px 8px', color: '#334155', fontWeight: '600' }}>Traditional Banks<br /><span style={{ fontSize: '12px', fontWeight: '400' }}>(HSBC, BOC, Citi)</span></td>
                      <td style={{ padding: '12px 8px', color: '#475569' }}>
                        • High brand credibility<br />
                        • Extensive branch network<br />
                        • Attractive new funds offers
                      </td>
                      <td style={{ padding: '12px 8px', color: '#475569' }}>
                        • Generally lower rates<br />
                        • Higher minimum deposit thresholds
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                2. Watch Out for New Funds Promotions
              </h3>
              <p style={{ marginBottom: '15px', color: '#475569' }}>
                Many banks offer special rates for "new funds," typically 0.2–0.5% higher than standard rates. New funds generally defined as:
              </p>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li><strong>Funds transferred from other banks</strong></li>
                <li><strong>Funds not deposited in that bank within a specific period (e.g., 3 months)</strong></li>
                <li><strong>Meeting minimum amount requirements (e.g., HK$10,000 or more)</strong></li>
              </ul>

              <div style={{
                background: '#eff6ff', border: '1px solid #3b82f6',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1e40af' }}>
                  💡 New Funds Fixed Deposit Tips
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '1.8', marginBottom: 0 }}>
                  <li>Understand the "new funds" definition before depositing</li>
                  <li>Note promotion expiry dates; rates may drop significantly afterward</li>
                  <li>Calculate early withdrawal penalties (if applicable)</li>
                  <li>Consider spreading deposits across 2–3 banks</li>
                </ul>
              </div>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                3. Choosing Deposit Tenure
              </h3>
              <ul style={{ marginLeft: '25px', marginBottom: '20px', color: '#475569' }}>
                <li><strong>1–3 months:</strong> Suitable for short-term parking, maintains flexibility, but rates usually lower</li>
                <li><strong>6 months:</strong> Good balance between returns and flexibility</li>
                <li><strong>12 months:</strong> Locks in longer-term returns, but consider rate trends and funding needs</li>
                <li><strong>12+ months:</strong> Some virtual banks offer up to 24–48 month deposits, suitable for long-term savings</li>
              </ul>

              <h3 style={{ fontSize: '20px', fontWeight: '600', marginTop: '25px', marginBottom: '15px', color: '#334155' }}>
                4. Deposit Protection Scheme
              </h3>
              <div style={{
                background: '#fef2f2', border: '1px solid #fca5a5',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#991b1b' }}>
                  🛡️ Important: Deposit Protection Limit
                </h3>
                <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: '10px' }}>
                  The Hong Kong Deposit Protection Board provides up to <strong>HK$500,000</strong> protection per depositor per bank.
                </p>
                <p style={{ color: '#475569', lineHeight: '1.8', marginBottom: 0, fontWeight: '600' }}>
                  Recommendation: If deposits exceed HK$500,000, spread them across 2 or more banks to ensure full protection.
                </p>
              </div>

              {/* ─── Sources ─── */}
              <div style={{
                background: '#f0fdf4', border: '1px solid #86efac',
                borderRadius: '8px', padding: '20px', marginBottom: '30px'
              }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#166534' }}>
                  📋 Sources
                </h3>
                <ul style={{ marginLeft: '20px', color: '#475569', lineHeight: '2', marginBottom: 0, fontSize: '13px' }}>
                  <li><a href="https://hk.ulifestyle.com.hk/topic/detail/20053976/%E9%A6%99%E6%B8%AF%E9%8A%80%E8%A1%8C%E6%B8%AF%E5%85%83%E5%AE%9A%E6%9C%9F%E5%AD%98%E6%AC%BE%E5%88%A9%E7%8E%87%E6%AF%94%E8%BC%83-%E6%9C%80%E6%96%B0%E6%B8%AF%E5%85%83%E5%AE%9A%E6%9C%9F%E9%AB%98%E6%81%AF%E4%B9%8B%E9%81%B8-%E6%AF%8F%E6%97%A5%E6%9B%B4%E6%96%B0" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>U Lifestyle HKD Fixed Deposit Comparison</a> — Daily updates ({lastUpdated})</li>
                  <li><a href="https://www.stashaway.hk/r/best-hkd-time-deposit-interest-rates-and-offers" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>StashAway HK</a> — Best HKD time deposit rates &amp; offers</li>
                  <li><a href="https://www.syfe.com/hk/magazine/april-time-deposit-interest-rate-hk/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>Syfe HK</a> — Monthly HKD time deposit rate update</li>
                  <li><a href="https://www.hsbc.com.hk/accounts/products/time-deposits/" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>HSBC Official Website</a> — Time deposit product page</li>
                  <li><a href="https://www.bochk.com" target="_blank" rel="noopener noreferrer" style={{ color: '#2563eb' }}>BOC (HK) Official Website</a> — Time deposit rates</li>
                </ul>
              </div>

              <AmazonRecommendation isEnglish={isEnglish} />

              {/* ─── Disclaimer ─── */}
              <div style={{
                background: '#f8fafc', borderTop: '3px solid #3b82f6',
                borderRadius: '8px', padding: '20px', marginTop: '40px'
              }}>
                <p style={{ marginBottom: '10px', fontSize: '14px', color: '#64748b' }}>
                  <strong>Disclaimer:</strong> Interest rate data in this article is compiled from official bank websites and public comparison platforms (as of {lastUpdated}). Rates may change without notice and vary by customer type, deposit amount, tenure, and promotions. Always verify the latest rates and terms directly with banks before making deposits. This article is for educational and informational purposes only and does not constitute financial advice. Consult a licensed financial advisor for personalized guidance.
                </p>
                <p style={{ marginBottom: 0, fontSize: '14px', color: '#64748b' }}>
                  <strong>Data Source:</strong> Official bank websites, StashAway HK, Syfe HK &amp; public comparison platforms ({lastUpdated})
                </p>
              </div>
            </>
          )}
        </Box>
      </Container>
    </Box>
  );
}
